import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { ButtonSmall } from "@anwb/poncho/components/button";
import Form from "@anwb/poncho/components/form";
import FormBlock from "@anwb/poncho/components/form-block";
import FormContainerFieldSet from "@anwb/poncho/components/form-container-field-set";
import FormFieldButton from "@anwb/poncho/components/form-field-button";
import Grid from "@anwb/poncho/components/grid";
import Table from "@anwb/poncho/components/table";
import { Tooltip } from "@anwb/poncho/components/tooltip";

import { useAuthorizedRequest } from "../../helpers/api";
import { IBasispoortInstellingModel } from "../../types/basispoortDetails";
import { BrinvestToolTip } from "../BrinvestToolTip/BrinvestToolTip";
import FormField from "../FormField/FormField";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";

const searchFields = ["naam", "brincode", "adres", "plaatsnaam", "bestuurscode"] as const;
type ValidProps = (typeof searchFields)[number];
type NAWSearchQueryParams = Record<ValidProps, string>;

const defaultSearchValues: NAWSearchQueryParams = searchFields.reduce(
  (r, key): NAWSearchQueryParams => {
    return {
      ...r,
      [key]: "",
    };
  },
  {} as NAWSearchQueryParams,
);

function BasispoortSearch() {
  const [instellingen, setInstellingen] = useState<IBasispoortInstellingModel[] | undefined>(
    undefined,
  );
  const searchState = useState<NAWSearchQueryParams>(defaultSearchValues);
  const searchValues = searchState[0];

  const navigate = useNavigate();

  const doSearchRequest = useAuthorizedRequest<IBasispoortInstellingModel[]>();
  const doSearch = () => {
    setInstellingen(undefined);

    const nonEmptySearchValues = Object.entries(searchValues)
      .filter(([, value]) => {
        return value !== "";
      })
      .reduce((r, [key, value]) => {
        return {
          ...r,
          [key]: value,
        };
      }, {});

    doSearchRequest.setRequest({
      path: `/basispoort/nawsearch?${new URLSearchParams(nonEmptySearchValues).toString()}`,
      onSuccess: (data) => {
        setInstellingen(data);
      },
    });
  };

  return (
    <Grid>
      <Grid.Item>
        <Form onSubmit={() => doSearch()}>
          <FormBlock>
            <FormContainerFieldSet>
              <FormField useState={searchState} keyword="naam" label="Naam" />
              <FormField
                useState={searchState}
                keyword="brincode"
                label="Brinvest"
                sideComponent={<BrinvestToolTip />}
              />
            </FormContainerFieldSet>
            <FormContainerFieldSet>
              <FormField useState={searchState} keyword="adres" label="Adres" />
              <FormField useState={searchState} keyword="plaatsnaam" label="Plaatsnaam" />
            </FormContainerFieldSet>
            <FormContainerFieldSet>
              <FormField
                required={false}
                useState={searchState}
                keyword="bestuurscode"
                label="Bestuurscode"
                sideComponent={
                  <Tooltip>
                    A bestuurscode (or bestuursnummer) is a unique identification code assigned by
                    DUO (Dienst Uitvoering Onderwijs) to educational boards in the Netherlands. Each
                    board, responsible for managing multiple schools or institutions, is given its
                    own bestuursnummer.
                  </Tooltip>
                }
              />
            </FormContainerFieldSet>
            <FormFieldButton type="submit">Search</FormFieldButton>
          </FormBlock>
        </Form>
      </Grid.Item>
      <Grid.Item>
        {doSearchRequest.isLoading ? (
          <LoadingSpinner />
        ) : (
          <Table textAlign="center">
            <Table.Header>
              <Table.Row>
                <Table.HeaderColumn>ID</Table.HeaderColumn>
                <Table.HeaderColumn>Naam</Table.HeaderColumn>
                <Table.HeaderColumn>Brincode</Table.HeaderColumn>
                <Table.HeaderColumn>Straat</Table.HeaderColumn>
                <Table.HeaderColumn>Huisnummer</Table.HeaderColumn>
                <Table.HeaderColumn>Huisnummer Toevoeging</Table.HeaderColumn>
                <Table.HeaderColumn>Postcode</Table.HeaderColumn>
                <Table.HeaderColumn>Woonplaats</Table.HeaderColumn>
                <Table.HeaderColumn>Telefoon Net</Table.HeaderColumn>
                <Table.HeaderColumn>Telefoon Abon</Table.HeaderColumn>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {instellingen?.map((instelling) => {
                return (
                  <Table.Row key={instelling.id}>
                    <Table.Column>{instelling.id}</Table.Column>
                    <Table.Column>{instelling.naam}</Table.Column>
                    <Table.Column>
                      {instelling.brincode && (
                        <ButtonSmall
                          color="tertiary"
                          onClick={() => navigate(`/schools/${instelling.brincode ?? ""}`)}
                        >
                          {instelling.brincode}
                        </ButtonSmall>
                      )}
                    </Table.Column>
                    <Table.Column>{instelling.straat}</Table.Column>
                    <Table.Column>{instelling.huisnummer}</Table.Column>
                    <Table.Column>{instelling.huisnummerToevoeging}</Table.Column>
                    <Table.Column>{instelling.postcode}</Table.Column>
                    <Table.Column>{instelling.woonplaats}</Table.Column>
                    <Table.Column>{instelling.telefoonNet}</Table.Column>
                    <Table.Column>{instelling.telefoonAbon}</Table.Column>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        )}
      </Grid.Item>
    </Grid>
  );
}

export default BasispoortSearch;
